import { setCookie } from '@cian/utils/lib/browser/cookie';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { prepareNewbuildingBroker } from './utils/prepareNewbuildingBroker';
import { prepareTrackingParams } from './utils/prepareTrackingParams';
import { CONSULTING_FORM_SUBMITTED_COOKIE_NAME } from '../../constants/requestForm';
import {
  selectFormAdditionalServices,
  selectFormPhone,
  selectFormPhoneVerificationCode,
  selectFormPriceRange,
  selectFormRegion,
  selectFormRequirements,
  selectFormRoomCount,
  selectFormStep,
} from '../../selectors/requestFormV2';
import { selectUserPhoneConfirmed } from '../../selectors/user';
import { selectUtm } from '../../selectors/utm';
import { createConsultingRequest } from '../../services/consultingRequest';
import { setErrors, setFormStep } from '../../slices';
import { IThunkApi } from '../../types/redux';
import { EFormStep, IRegion } from '../../types/requestForm/requestFormV2';
import { clearPhone } from '../../utils/phone';
import { trackCallbackClickAction } from '../analytics/trackCallbackClickAction';
import { sendLegalAgreements } from '../legalAgreement';
import { requestPhoneVerificationCode } from '../phoneVerification/requestPhoneVerificationCode';
interface IThunkApiExtended extends IThunkApi {
  rejectValue: void;
}

export const createConsultingRequestWithVerification = createAsyncThunk<void, void, IThunkApiExtended>(
  'consultingRequest/createConsultingRequest',
  async (parameters, { extra: context, dispatch, getState }) => {
    const { httpApi } = context;
    const state = getState();
    const phone = selectFormPhone(state);
    const region = selectFormRegion(state);
    const requirements = selectFormRequirements(state);
    const roomCount = selectFormRoomCount(state);
    const priceRange = selectFormPriceRange(state);
    const additionalServices = selectFormAdditionalServices(state);
    const step = selectFormStep(state);
    const userPhoneConfirmed = selectUserPhoneConfirmed(state);
    const validationCode = selectFormPhoneVerificationCode(state);

    try {
      if (step === EFormStep.BookingExists) {
        return;
      }

      const preparedPhone = phone ? `+${clearPhone(phone)}` : '';
      const newbuildingBroker = prepareNewbuildingBroker(context.config);
      const utm = selectUtm(state) ?? undefined;

      if (!userPhoneConfirmed && !validationCode) {
        dispatch(requestPhoneVerificationCode());

        return;
      }

      await createConsultingRequest({
        httpApi,
        parameters: {
          source: 'landingKpn',
          phone: preparedPhone,
          regionId: region?.id,
          requirements,
          roomCount,
          priceRange,
          additionalServices,
          newbuildingBroker,
          utm,
          validationCode,
        },
      });

      // Устанавливаем куку на сутки о том, что форма отправлена
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setCookie(CONSULTING_FORM_SUBMITTED_COOKIE_NAME, '1', { expires: tomorrow });

      dispatch(
        trackCallbackClickAction(
          prepareTrackingParams({ phone: phone ?? '', region: region as IRegion, requirements: requirements ?? '' }),
        ),
      );
      // Отправляем согласие на обработку персональных данных и маркетинговых материалов
      dispatch(sendLegalAgreements());
      dispatch(setFormStep(EFormStep.Success));
    } catch (error) {
      const {
        customerPhone,
        missingValidationCode,
        invalidCustomerPhone,
        invalidValidationCode,
        expiredValidationCode,
      } = error.details;
      if (missingValidationCode) {
        dispatch(requestPhoneVerificationCode());
        dispatch(setFormStep(EFormStep.PhoneVerification));

        return;
      }

      if (invalidValidationCode || expiredValidationCode) {
        dispatch(setFormStep(EFormStep.PhoneVerification));
      }

      if (invalidCustomerPhone || customerPhone) {
        dispatch(setFormStep(EFormStep.Contacts));
      }

      if (
        !customerPhone &&
        !invalidCustomerPhone &&
        !invalidValidationCode &&
        !missingValidationCode &&
        !expiredValidationCode
      ) {
        dispatch(setFormStep(EFormStep.Error));
      }

      dispatch(
        setErrors({
          phone: customerPhone || invalidCustomerPhone,
          phoneVerification: invalidValidationCode || expiredValidationCode,
        }),
      );
    }
  },
);
